import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";
import axios from "./axiosInstance";
import { AIReview, AIReviewRequest, AIReviewStatus, PromptType, AIReviewFeedback, FeedbackItem, LlmFailureCode } from "common/models/types";
import { getDateStringFromToday } from "utils/TimeUtil";
import { store } from "../store";

// API 응답 기본 인터페이스
export interface ApiResponse<T> {
  error: number; // 0은 성공
  message?: string;
  data?: T;
}

// 워커 상태 인터페이스
export interface WorkerStatus {
  reviewId: number;
  status: 'waiting' | 'processing' | 'completed' | 'failed' | number; // Backend에서 숫자로도 올 수 있음
  queuePosition?: number;
  totalQueue?: number;
  queueInfo?: {
    totalPending: number;
    activeWorkers: number;
    maxWorkers: number;
  };
  workerAvailable?: boolean;
  isProcessing?: boolean;
  estimatedTimeSeconds?: number;
  estimatedWaitTime?: number; // Backend가 이 필드명도 사용
  message?: string;
}

/**
 * 분석 요청이 실패했을 때 화면에 그대로 오르는 고정 문구.
 * spec 2026-08-31-phai-웹-LLM실패-사용자안내 #1 — 서버 원문(영문 포함)을 사용자 문구 자리에 쓰지 않는다.
 */
export const ANALYSIS_REQUEST_FAILED_MESSAGE = "분석 요청에 실패했어요. 잠시 후 다시 시도해 주세요.";

// 스트림 이벤트 타입
export type StreamEventCallback = (review: AIReview) => void;
export type StreamStatusCallback = (
  status: AIReviewStatus,
  message: string,
  queueData?: any,
  /** FAILED(-1) 이벤트에만 실리는 LLM 실패 사유 코드. 값이 없는 실패는 null. */
  llmFailureCode?: LlmFailureCode | null
) => void;
export type StreamErrorCallback = (error: any) => void;
export type StreamCompleteCallback = () => void;

/**
 * AI 리뷰 업데이트 (reviewResultEdited 필드)
 * @param aiReviewId AI 리뷰 ID
 * @param reviewResultEdited 수정된 리뷰 내용
 */
export const updateAIReviewEdited = async (
  aiReviewId: number,
  reviewResultEdited: string
): Promise<AIReview> => {
  try {
    const response = await axios.put(`/api/v1/ai-reviews/${aiReviewId}`, {
      reviewResultEdited
    });
    return response.data.data;
  } catch (error) {
    console.error('AI 리뷰 수정 저장 실패:', error);
    throw error;
  }
};

/**
 * AI 리뷰 수정 내용 삭제
 * @param aiReviewId AI 리뷰 ID
 */
export const deleteAIReviewEdited = async (aiReviewId: number): Promise<AIReview> => {
  try {
    const response = await axios.put(`/api/v1/ai-reviews/${aiReviewId}`, {
      reviewResultEdited: null
    });
    return response.data.data;
  } catch (error) {
    console.error('AI 리뷰 수정 내용 삭제 실패:', error);
    throw error;
  }
};

/**
 * 처방전 분석 요청 API
 * @param data 분석 요청 데이터
 * @param isPremium Premium 사용자 여부 (과금 면제)
 * @returns 분석 요청 결과
 */
export const analyzePrescriptions = async (
  data: AIReviewRequest,
  isPremium: boolean = false
): Promise<
  ApiResponse<{ reviewId: number; status: number; queuePosition: number; message: string }>
> => {
  try {
    const requestData = {
      ...data,
      skipPayment: isPremium  // Premium 사용자는 과금 면제
    };
    const response = await axios.post("/api/v1/ai-reviews/analyze", requestData);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "처방전 분석 요청 실패:", error);
    return {
      error: 1,
      message: ANALYSIS_REQUEST_FAILED_MESSAGE,
    };
  }
};

/**
 * 분석 결과 조회 API
 * @param analysisId 분석 ID
 * @returns 분석 결과
 */
export const fetchAnalysisResult = async (analysisId: string): Promise<ApiResponse<AIReview>> => {
  try {
    const response = await axios.get(`/api/v1/ai-reviews/${analysisId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "분석 결과 조회 실패:", error);
    return {
      error: 1,
      message: "분석 결과 조회에 실패했습니다",
    };
  }
};

/**
 * 분석 상태 조회 API
 * @param analysisId 분석 ID
 * @returns 분석 상태
 */
export const fetchAnalysisStatus = async (
  analysisId: string
): Promise<
  ApiResponse<{ status: "pending" | "processing" | "completed" | "failed"; progress?: number }>
> => {
  try {
    const response = await axios.get(`/api/v1/ai-reviews/${analysisId}/status`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "분석 상태 조회 실패:", error);
    return {
      error: 1,
      message: "분석 상태 조회에 실패했습니다",
    };
  }
};

/**
 * 환자의 최근 분석 결과 목록 조회 API
 * @param patientId 환자 ID
 * @returns 분석 결과 목록
 */
export const fetchPatientAnalysisHistory = async (
  patientId: number
): Promise<ApiResponse<AIReview[]>> => {
  try {
    const response = await axios.get(`/api/v1/ai-reviews?patientId=${patientId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "환자 분석 이력 조회 실패:", error);
    return {
      error: 1,
      message: "환자 분석 이력 조회에 실패했습니다",
    };
  }
};

export const fetchAnalysisHistory = async (): Promise<ApiResponse<AIReview[]>> => {
  try {
    // 최근 보고서: 기간 제한 없이 createdAt DESC 최신순 limit 10건 (핀 여부 무관)
    const response = await axios.get(
      `/api/v1/ai-reviews?limit=10&orderBy=createdAt&orderDirection=DESC`,
    );
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "분석 이력 조회 실패:", error);
    return {
      error: 1,
      message: "분석 이력 조회에 실패했습니다",
    };
  }
};

/**
 * 핀 보고서 대시보드 요약 조회 API (상위 10건, startDate/endDate 무시됨)
 */
export const fetchPinnedReviews = async (): Promise<ApiResponse<AIReview[]>> => {
  try {
    const response = await axios.get("/api/v1/ai-reviews?pinnedOnly=true&limit=10");
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "핀 보고서 조회 실패:", error);
    return {
      error: 1,
      message: "핀 보고서 조회에 실패했습니다",
    };
  }
};

export interface FetchAiReviewsParams {
  pinnedOnly?: boolean;
  patientId?: number;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  status?: number | number[];
  orderBy?: string;
  orderDirection?: "ASC" | "DESC";
}

/**
 * AI 리뷰 목록 범용 조회 API (필터 + 페이지네이션 지원)
 * 보고서 관리 페이지 등 페이지네이션이 필요한 곳에서 사용.
 */
export const fetchAiReviews = async (
  params: FetchAiReviewsParams = {},
): Promise<ApiResponse<AIReview[]>> => {
  try {
    const query = new URLSearchParams(params as any).toString();
    const response = await axios.get(`/api/v1/ai-reviews${query ? `?${query}` : ""}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI 리뷰 목록 조회 실패:", error);
    return {
      error: 1,
      message: "AI 리뷰 목록 조회에 실패했습니다",
    };
  }
};

export interface AiReviewCountParams {
  patientId?: number;
  startDate?: string;
  endDate?: string;
  pinnedOnly?: boolean;
  status?: number | number[];
  admin?: boolean;
}

/**
 * AI 리뷰 전체 건수 조회 API (ORDER BY / LIMIT / OFFSET 무시)
 */
export const fetchAiReviewCount = async (
  params?: AiReviewCountParams,
): Promise<ApiResponse<{ total: number }>> => {
  try {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : "";
    const response = await axios.get(`/api/v1/ai-reviews/count${query}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI 리뷰 건수 조회 실패:", error);
    return {
      error: 1,
      message: "AI 리뷰 건수 조회에 실패했습니다",
    };
  }
};

/**
 * 워커 상태 조회 API
 * @param reviewId 리뷰 ID
 * @returns 워커 상태 정보
 */
export const fetchWorkerStatus = async (reviewId: number): Promise<WorkerStatus> => {
  try {
    const response = await axios.get(`/api/v1/ai-reviews/${reviewId}/worker-status`);
    return response.data.data;
  } catch (error) {
    log(LogLevel.ERROR, `워커 상태 조회 실패: ${reviewId}`, error);
    throw error;
  }
};

/**
 * AI 리뷰 상세 정보 조회 API
 * @param reviewId 리뷰 ID
 * @returns AI 리뷰 상세 정보
 */
export const fetchAIReviewById = async (reviewId: number): Promise<ApiResponse<AIReview>> => {
  try {
    log(LogLevel.INFO, `[fetchAIReviewById] Fetching review data for ID: ${reviewId}`);
    const response = await axios.get(`/api/v1/ai-reviews/${reviewId}`);
    log(LogLevel.INFO, `[fetchAIReviewById] Successfully fetched review ${reviewId}`, {
      status: response.data.data?.status,
      hasResult: !!response.data.data?.reviewResult
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, `[fetchAIReviewById] Failed to fetch review ${reviewId}:`, error);
    throw error;
  }
};

export class AiReviewStreamer {
  onReview: StreamEventCallback;
  onDone: StreamCompleteCallback;
  onFail: StreamErrorCallback;
  onStatus: StreamStatusCallback;
  id: number = 0;
  eventSource: EventSource = null;
  reviewResult: string = "";
  private retryCount: number = 0;
  private readonly maxRetries: number = 3;
  private readonly retryDelay: number = 1000; // 초기 재시도 지연 (ms)
  private isClosing: boolean = false;

  constructor(id, onRecieve, onDone, onFail, onStatus) {
    log(LogLevel.INFO, "AiReviewStreamer:생성자 호출", id);
    this.id = id;
    this.onReview = onRecieve;
    this.onDone = onDone;
    this.onFail = onFail;
    this.onStatus = onStatus;

    this.connect();
  }

  private connect() {
    // Redux store에서 현재 리뷰 상태 확인
    const state = store.getState();
    const review = state.data.reviews[this.id];
    
    // 이미 완료되거나 실패한 리뷰는 연결하지 않음
    if (review && (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED)) {
      log(LogLevel.INFO, `[AiReviewStreamer] Skipping connection for review ${this.id} - status: ${review.status === AIReviewStatus.COMPLETED ? 'COMPLETED' : 'FAILED'}`);
      this.close();
      return;
    }
    
    const url =
      (process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : "") +
      `/api/v1/ai-reviews/${this.id}/stream`;
    log(LogLevel.INFO, "EventSource URL:", url);

    this.eventSource = new EventSource(url, {
      withCredentials: true,
    });

    log(LogLevel.INFO, "EventSource 생성됨, readyState:", this.eventSource.readyState);

    this.eventSource.onopen = (event) => {
      log(
        LogLevel.INFO,
        "AiReviewStreamer:연결됨",
        this.id,
        "readyState:",
        this.eventSource.readyState
      );
      
      // Redux store에서 현재 리뷰 상태 재확인
      const state = store.getState();
      const review = state.data.reviews[this.id];
      
      // 완료/실패 상태면 연결 성공해도 바로 종료
      if (review && (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED)) {
        log(LogLevel.INFO, `[AiReviewStreamer] Closing immediately - review ${this.id} already ${review.status === AIReviewStatus.COMPLETED ? 'COMPLETED' : 'FAILED'}`);
        this.close();
        return;
      }
      
      // 정상적인 연결인 경우만 재시도 카운트 리셋
      this.retryCount = 0;
    };

    this.eventSource.onmessage = this._onMessage;
    this.eventSource.onerror = this._onError;
    
    // 'error' 이벤트 타입의 메시지를 처리하는 리스너 추가
    this.eventSource.addEventListener('error', (event: MessageEvent) => {
      log(LogLevel.INFO, `[AiReviewStreamer] Received 'error' event for review ${this.id}: ${event.data}`);
      
      // "작업을 찾을 수 없습니다" 같은 메시지 처리
      if (event.data && (
        event.data.includes('작업을 찾을 수 없습니다') || 
        event.data.includes('이미 완료') ||
        event.data.includes('존재하지 않')
      )) {
        log(LogLevel.INFO, `[AiReviewStreamer] Task not found or already completed for review ${this.id}, stopping retries`);
        this.retryCount = this.maxRetries; // 재시도 방지
        this.close();
      }
    });
  }

  private reconnect() {
    if (this.isClosing) {
      log(LogLevel.INFO, `AiReviewStreamer:재연결 중단 - 명시적 종료 중 (id: ${this.id})`);
      return;
    }

    // Redux store에서 현재 리뷰 상태 확인
    const state = store.getState();
    const review = state.data.reviews[this.id];
    
    // 이미 완료되거나 실패한 리뷰는 재연결하지 않음
    if (review && (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED)) {
      log(LogLevel.INFO, `[AiReviewStreamer] Skipping reconnect for review ${this.id} - status: ${review.status === AIReviewStatus.COMPLETED ? 'COMPLETED' : 'FAILED'}`);
      this.close();
      return;
    }

    // 기존 연결 정리
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // 재연결
    log(LogLevel.INFO, `AiReviewStreamer:재연결 시도 (id: ${this.id})`);
    this.connect();
  }

  /**
   * 스트리머 연결을 종료하고 리소스를 정리합니다.
   */
  close() {
    log(LogLevel.INFO, `AiReviewStreamer:close 호출됨 - id: ${this.id}`);
    
    // 재연결 방지 플래그 설정
    this.isClosing = true;

    // 콜백 함수들 정리
    this.onReview = null;
    this.onDone = null;
    this.onFail = null;
    this.onStatus = null;

    if (this.eventSource) {
      log(LogLevel.VERBOSE, `EventSource 종료 중 - readyState: ${this.eventSource.readyState}`);
      this.eventSource.close();
      this.eventSource = null;
      log(LogLevel.VERBOSE, `EventSource 종료 완료`);
    }

    reviewStreamPool.delete(this.id);

    log(LogLevel.INFO, `AiReviewStreamer:리소스 정리 완료 - id: ${this.id}`);
  }

  private _onMessage = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      log(LogLevel.INFO, "AiReviewStreamer:_onMessage", this.id, data);
      if (data.type === "review") {
        // 리뷰 데이터 수신 시 상태 및 결과 확인
        // status가 READY(200)이지만 reviewResult가 있으면 이미 완료된 리뷰
        if (data.review && (
          data.review.status === AIReviewStatus.COMPLETED || 
          data.review.status === AIReviewStatus.FAILED ||
          (data.review.status === AIReviewStatus.READY && data.review.reviewResult && data.review.reviewResult.length > 0)
        )) {
          log(LogLevel.INFO, `[AiReviewStreamer] Review ${this.id} appears to be completed (status: ${data.review.status}, has result: ${!!data.review.reviewResult}), closing stream`);
          this.retryCount = this.maxRetries; // 재시도 방지
          if (this.onReview) {
            this.onReview(data.review);
          }
          this.close();
          return;
        }
        
        if (this.onReview) {
          this.onReview(data.review);
        }
      } else if (data.type === "status") {
        // 완료/실패 상태 감지 - 서버가 완료된 리뷰에 대해 상태를 전송하는 경우
        if (data.status === AIReviewStatus.COMPLETED || data.status === AIReviewStatus.FAILED) {
          log(LogLevel.INFO, `[AiReviewStreamer] Received ${data.status === AIReviewStatus.COMPLETED ? 'COMPLETED' : 'FAILED'} status for review ${this.id}`);
          
          // Redux 상태 업데이트를 위해 onStatus 콜백 호출
          // 실패 사유 코드(llmFailureCode)는 이 초입에서 넘기지 않으면 이후 어디에서도 복구할 수 없다.
          if (this.onStatus) {
            this.onStatus(data.status, data.message, undefined, data.llmFailureCode ?? null);
          }
          
          // 재시도 방지 및 스트림 종료
          this.retryCount = this.maxRetries;
          this.close();
          return;
        }
        
        // PENDING 상태 처리 (Backend에서 대기열 정보와 함께 전송)
        if (data.status === AIReviewStatus.PENDING) {
          log(LogLevel.INFO, `[AiReviewStreamer] 🚦 QUEUE DETECTED for review ${this.id}:`, {
            status: data.status,
            message: data.message,
            queuePosition: data.queuePosition,
            queueInfo: data.queueInfo,
            estimatedWaitTime: data.estimatedWaitTime,
            pollingUrl: data.pollingUrl,
            recommendedInterval: data.recommendedInterval
          });
          
          // Queue 정보를 포함한 status 콜백 호출
          if (this.onStatus) {
            this.onStatus(data.status, data.message, {
              queuePosition: data.queuePosition,
              queueInfo: data.queueInfo,
              estimatedTimeSeconds: data.estimatedWaitTime,
              pollingUrl: data.pollingUrl,
              recommendedInterval: data.recommendedInterval
            });
          }
          
          // SSE 연결 종료하여 HybridStreamManager가 Polling으로 전환하도록 함
          log(LogLevel.INFO, `[AiReviewStreamer] Closing SSE connection for review ${this.id} to switch to polling mode`);
          this.close();
        } else {
          // 일반 status 메시지 처리
          log(LogLevel.INFO, `[AiReviewStreamer] Status update for review ${this.id}: ${data.status} - ${data.message?.substring(0, 50)}...`);
          if (this.onStatus) {
            this.onStatus(data.status, data.message);
          }
        }
      } else if (data.type === "finished") {
        log(LogLevel.INFO, `[AiReviewStreamer] Received 'finished' for review ${this.id}`);
        // 재시도 방지를 위해 카운트를 최대값으로 설정
        this.retryCount = this.maxRetries;
        if (this.onDone) {
          this.onDone();
        }
        this.close();
      } else if (data.type === "error") {
        log(LogLevel.INFO, `[AiReviewStreamer] Received 'error' for review ${this.id}: ${data.error}`);
        // 재시도 방지를 위해 카운트를 최대값으로 설정
        this.retryCount = this.maxRetries;
        if (this.onFail) {
          this.onFail(data.error);
        }
        this.close();
      } else if (data.type === "chunk") {
        this.reviewResult += data.chunk;
        if (this.onReview) {
          this.onReview({ id: this.id, reviewResult: this.reviewResult });
        }
      }
    } catch (error) {
      log(LogLevel.ERROR, "AiReviewStreamer:_onMessage 에러 발생", error);
    }
  };

  private _onError = (err: Event) => {
    // Redux store에서 현재 리뷰 상태 확인
    const state = store.getState();
    const review = state.data.reviews[this.id];
    
    // 완료/실패 상태면 에러가 발생해도 재시도하지 않음
    if (review && (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED)) {
      log(LogLevel.INFO, `[AiReviewStreamer] Ignoring error for completed/failed review ${this.id} (status: ${review.status})`);
      this.close();
      return;
    }
    
    // EventSource readyState 정보
    // 0: CONNECTING, 1: OPEN, 2: CLOSED
    const readyStateText =
      this.eventSource?.readyState === 0
        ? "CONNECTING"
        : this.eventSource?.readyState === 1
        ? "OPEN"
        : "CLOSED";
    
    log(LogLevel.ERROR, `[AiReviewStreamer] ❌ ERROR for review ${this.id}:`, {
      readyState: `${readyStateText}(${this.eventSource?.readyState})`,
      url: this.eventSource?.url,
      retryCount: `${this.retryCount}/${this.maxRetries}`,
      isClosing: this.isClosing,
      reviewStatus: review?.status,
      error: err
    });

    // 재시도 로직
    if (this.retryCount < this.maxRetries && !this.isClosing) {
      this.retryCount++;
      const delay = this.retryDelay * Math.pow(2, this.retryCount - 1); // 지수 백오프
      
      log(LogLevel.INFO, 
        `[AiReviewStreamer] 🔄 Retry ${this.retryCount}/${this.maxRetries} in ${delay}ms for review ${this.id}`
      );
      
      setTimeout(() => {
        log(LogLevel.INFO, `[AiReviewStreamer] Executing retry ${this.retryCount} for review ${this.id}`);
        this.reconnect();
      }, delay);
    } else {
      // 최대 재시도 횟수 초과 또는 명시적 종료 중
      if (!this.isClosing) {
        log(LogLevel.ERROR, `[AiReviewStreamer] ⛔ Max retries exceeded for review ${this.id} - closing stream`);
      } else {
        log(LogLevel.INFO, `[AiReviewStreamer] Stream closing intentionally for review ${this.id}`);
      }
      
      // close()가 콜백들을 비우므로 먼저 붙잡아 둔다 — 그러지 않으면 재시도 소진 신호가 사라져
      // 화면이 무표시로 남는다(끊김 UI가 이 콜백 하나에 걸려 있다).
      const onFail = this.onFail;
      this.close();
      if (onFail) {
        onFail(err);
      }
    }
  };
}

export const reviewStreamPool = new Map<number, AiReviewStreamer>();

/**
 * 분석 결과 실시간 스트림 API
 * Server-Sent Events(SSE)를 사용하여 실시간으로 분석 결과를 스트리밍 받습니다.
 *
 * @param analysisId 분석 ID
 * @param onEvent 스트림 데이터 수신 시 호출될 콜백 함수
 * @param onError 에러 발생 시 호출될 콜백 함수
 * @param onComplete 스트림이 완료되었을 때 호출될 콜백 함수
 * @returns 이벤트 소스를 닫는 함수
 */
export const fetchAnalysisStream = (
  analysisId: number,
  onStatus: StreamStatusCallback,
  onReview: StreamEventCallback,
  onError?: StreamErrorCallback,
  onComplete?: StreamCompleteCallback
) => {
  log(LogLevel.INFO, `fetchAnalysisStream 호출됨 - analysisId: ${analysisId}`);

  try {
    // SSE 연결을 위한 EventSource 생성
    log(LogLevel.VERBOSE, `스트리머 풀에서 검색 중 - analysisId: ${analysisId}`);
    let streamer = reviewStreamPool.get(analysisId);
    log(LogLevel.VERBOSE, `스트리머 검색 결과:`, streamer ? `존재함 (id: ${streamer.id})` : "없음");

    // 기존 스트리머가 있지만 EventSource가 닫혀있으면 제거
    if (streamer && (!streamer.eventSource || streamer.eventSource.readyState === EventSource.CLOSED)) {
      log(LogLevel.INFO, `[fetchAnalysisStream] Removing closed streamer for review ${analysisId} (readyState: ${streamer.eventSource?.readyState})`);
      reviewStreamPool.delete(analysisId);
      streamer = null;
    }

    if (!streamer) {
      log(LogLevel.INFO, `새로운 AiReviewStreamer 생성 - analysisId: ${analysisId}`);
      log(LogLevel.VERBOSE, `AiReviewStreamer 생성자 호출 직전`);
      //onRecieve, onDone, onFail, onStatus
      streamer = new AiReviewStreamer(analysisId, onReview, onComplete, onError, onStatus);
      log(LogLevel.VERBOSE, `AiReviewStreamer 생성자 호출 완료`);
      reviewStreamPool.set(analysisId, streamer);
      log(LogLevel.VERBOSE, `스트리머 풀에 추가됨 - 현재 풀 크기: ${reviewStreamPool.size}`);
    } else {
      log(LogLevel.INFO, `기존 AiReviewStreamer 재사용 - analysisId: ${analysisId}`);
      streamer.onStatus = onStatus;
      streamer.onReview = onReview;
      streamer.onFail = onError;
      streamer.onDone = onComplete;
      log(LogLevel.VERBOSE, `스트리머 콜백 함수들 업데이트 완료`);
    }

    log(LogLevel.VERBOSE, `fetchAnalysisStream 완료 - analysisId: ${analysisId}`);
    // 연결 종료 함수 반환
    return;
  } catch (error) {
    log(LogLevel.ERROR, `fetchAnalysisStream 에러 발생 - analysisId: ${analysisId}`, error);
    if (onError) {
      onError(error);
    }
    return;
  }
};

/**
 * 분석 결과 실시간 스트림을 중단합니다.
 * 해당 분석 ID의 스트리머 연결을 종료하고 풀에서 제거합니다.
 *
 * @param analysisId 중단할 분석 ID
 * @returns 성공 여부
 */
export const stopAnalysisStream = (analysisId: number): boolean => {
  log(LogLevel.INFO, `stopAnalysisStream 호출됨 - analysisId: ${analysisId}`);

  try {
    // 스트리머 풀에서 해당 분석 ID의 스트리머 검색
    log(LogLevel.VERBOSE, `스트리머 풀에서 검색 중 - analysisId: ${analysisId}`);
    const streamer = reviewStreamPool.get(analysisId);

    if (!streamer) {
      log(LogLevel.WARNING, `스트리머를 찾을 수 없음 - analysisId: ${analysisId}`);
      return false;
    }

    log(LogLevel.INFO, `스트리머 찾음, 연결 종료 중 - analysisId: ${analysisId}`);

    // 스트리머 연결 종료 및 리소스 정리
    streamer.close();

    // 스트리머 풀에서 제거
    log(
      LogLevel.INFO,
      `스트리머 풀에서 제거됨 - analysisId: ${analysisId}, 현재 풀 크기: ${reviewStreamPool.size}`
    );

    log(LogLevel.INFO, `stopAnalysisStream 성공 - analysisId: ${analysisId}`);
    return true;
  } catch (error) {
    log(LogLevel.ERROR, `stopAnalysisStream 에러 발생 - analysisId: ${analysisId}`, error);
    return false;
  }
};

// 환자 정보 삭제 (소프트 딜리트)
export async function deleteAIReview(reviewId: number): Promise<ApiResponse<AIReview>> {
  try {
    const response = await axios.delete(`/api/v1/ai-reviews/${reviewId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "분석결과 삭제 실패:", error);
    return {
      error: 1,
      message: "분석결과 삭제에 실패했습니다.",
    };
  }
}

/**
 * AI Review 정보 업데이트 API
 * @param reviewId 리뷰 ID
 * @param data 업데이트할 데이터
 * @returns 업데이트된 AI Review
 */
export interface UpdateAIReviewDto {
  pinnedAt?: Date | null | number;
  memo?: string;
  premiumRole?: string;
}

export async function updateAIReview(
  reviewId: number,
  data: UpdateAIReviewDto
): Promise<ApiResponse<AIReview>> {
  try {
    const response = await axios.put(`/api/v1/ai-reviews/${reviewId}`, data);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI Review 업데이트 실패:", error);
    return {
      error: 1,
      message: "AI Review 업데이트에 실패했습니다.",
    };
  }
}

/**
 * 관리자용 AI Review 목록 조회 API
 * @param params 조회 파라미터
 * @returns AI Review 목록
 */
export interface AdminAIReviewListParams {
  startDate: string;
  endDate: string;
  start?: number;
  count?: number;
  withEvaluationId?: boolean; // 최신 SUCCESS 평가 ID 포함 여부
  includeDeleted?: boolean; // 삭제된 항목 포함 여부
}

export const fetchAdminAIReviewsList = async (
  params: AdminAIReviewListParams
): Promise<ApiResponse<any[]>> => {
  try {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
      start: (params.start || 0).toString(),
      count: (params.count || 100).toString(),
    });
    
    // withEvaluationId 파라미터 추가
    if (params.withEvaluationId !== undefined) {
      queryParams.append('withEvaluationId', params.withEvaluationId.toString());
    }
    
    // includeDeleted 파라미터 추가
    if (params.includeDeleted !== undefined) {
      queryParams.append('includeDeleted', params.includeDeleted.toString());
    }

    const response = await axios.get(`/api/v1/ai-reviews/admin/list?${queryParams}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "관리자 AI Review 목록 조회 실패:", error);
    return {
      error: 1,
      message: "AI Review 목록 조회에 실패했습니다",
      data: [],
    };
  }
};

/**
 * 관리자용 AI Review LLM 재분석 API
 * @param reviewId AI 리뷰 ID
 * @returns 재분석 결과
 */
export const retryAIReviewLLM = async (reviewId: number): Promise<ApiResponse<AIReview>> => {
  try {
    const response = await axios.post(`/api/v1/ai-reviews/admin/${reviewId}/retry-llm`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI Review LLM 재분석 실패:", error);
    return {
      error: 1,
      message: "AI Review LLM 재분석에 실패했습니다.",
    };
  }
};

/**
 * 관리자용 AI Review 상태 변경 API
 * @param reviewId AI 리뷰 ID
 * @param status 변경할 상태
 * @returns 상태 변경 결과
 */
export const updateAdminAIReviewStatus = async (
  reviewId: number,
  status: AIReviewStatus
): Promise<ApiResponse<AIReview>> => {
  try {
    const response = await axios.put(`/api/v1/ai-reviews/admin/${reviewId}/status`, { status });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI Review 상태 변경 실패:", error);
    return {
      error: 1,
      message: "AI Review 상태 변경에 실패했습니다.",
    };
  }
};

// ==================== AI Review 피드백 API ====================

/**
 * 피드백 생성 (별점 제출)
 * UPSERT 방식 - 기존 피드백이 있으면 업데이트
 */
export const createAIReviewFeedback = async (
  aiReviewId: number,
  rating: number
): Promise<ApiResponse<AIReviewFeedback>> => {
  try {
    const response = await axios.post(`/api/v1/ai-reviews/${aiReviewId}/feedback`, { rating });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "피드백 생성 실패:", error);
    throw error;
  }
};

/**
 * 피드백 수정 (상세 의견 추가)
 */
export const updateAIReviewFeedback = async (
  aiReviewId: number,
  data: {
    rating?: number;
    likes?: FeedbackItem[];
    dislikes?: FeedbackItem[];
    comment?: string;
  }
): Promise<ApiResponse<AIReviewFeedback>> => {
  try {
    const response = await axios.patch(`/api/v1/ai-reviews/${aiReviewId}/feedback`, data);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "피드백 수정 실패:", error);
    throw error;
  }
};

/**
 * 피드백 조회 (선택적)
 *
 * ⚠️ 현재 구현에서는 사용하지 않음
 * - feedbackId가 있으면 UI를 숨기므로 상세 데이터 조회 불필요
 * - 향후 피드백 수정 기능 등 추가 시 활용 가능
 *
 * @returns 피드백 데이터 또는 null (404인 경우)
 */
export const getAIReviewFeedback = async (
  aiReviewId: number
): Promise<AIReviewFeedback | null> => {
  try {
    const response = await axios.get(`/api/v1/ai-reviews/${aiReviewId}/feedback`);
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;  // 피드백 없음은 정상 케이스
    }
    log(LogLevel.ERROR, "피드백 조회 실패:", error);
    throw error;
  }
};

// ==================== Admin 피드백 관리 API ====================

/**
 * 관리자용 피드백 목록 조회 파라미터
 */
export interface AdminFeedbackListParams {
  startDate?: string;
  endDate?: string;
  rating?: number;
  aiReviewId?: number;
  userId?: string;
  start?: number;
  count?: number;
}

/**
 * 관리자용 피드백 목록 조회 API
 * @param params 조회 파라미터
 * @returns 피드백 목록 (AIReview, User 관계 필드 포함)
 */
export const fetchAdminFeedbackList = async (
  params: AdminFeedbackListParams
): Promise<ApiResponse<AIReviewFeedback[]>> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.rating !== undefined) queryParams.append('rating', params.rating.toString());
    if (params.aiReviewId) queryParams.append('aiReviewId', params.aiReviewId.toString());
    if (params.userId) queryParams.append('userId', params.userId);
    queryParams.append('start', (params.start || 0).toString());
    queryParams.append('count', (params.count || 100).toString());

    const response = await axios.get(`/api/v1/admin/ai-reviews/feedback?${queryParams}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "관리자 피드백 목록 조회 실패:", error);
    return {
      error: 1,
      message: "피드백 목록 조회에 실패했습니다",
      data: [],
    };
  }
};